import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Input, RadioGroup } from '@/components/ui';
import type {
  AdminClaimProcessRequest,
  ClaimReason,
} from '@/lib/api-admin/types';
import type { ClaimActionType } from '../lib/claimActionRbac';
import { validateClaimAction } from '../schemas/adminClaimProcessSchema';

const REJECT_REASON_OPTIONS: ReadonlyArray<ClaimReason> = [
  'DEFECTIVE',
  'WRONG_DELIVERY',
  'MIND_CHANGE',
  'SHIPPING_DELAY',
  'OTHER',
];

interface Money {
  amount: number;
  currencyCode: string;
}

const formatMoney = (m: Money | null | undefined): string => {
  if (!m) return '-';
  return `${m.amount.toLocaleString()} ${m.currencyCode}`;
};

export interface ClaimActionModalProps {
  action: ClaimActionType;
  claimId: string;
  orderNo: string;
  expectedRefundAmount: Money | null;
  isOpen: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onSubmit: (body: AdminClaimProcessRequest) => void;
  onCancel: () => void;
}

export const ClaimActionModal = ({
  action,
  claimId,
  orderNo,
  expectedRefundAmount,
  isOpen,
  isSubmitting,
  errorMessage,
  onSubmit,
  onCancel,
}: ClaimActionModalProps) => {
  const { t } = useTranslation('claims');
  const [note, setNote] = useState('');
  const [reasonCode, setReasonCode] = useState<ClaimReason>('DEFECTIVE');
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [overrideAmount, setOverrideAmount] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setNote('');
      setReasonCode('DEFECTIVE');
      setOverrideEnabled(false);
      setOverrideAmount('');
      setLocalError(null);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const values = {
      note: note.trim(),
      reasonCode: action === 'reject' ? reasonCode : '',
      refundOverrideAmount: undefined,
    };
    const result = validateClaimAction(action, values);
    if (!result.ok) {
      setLocalError(t(`claims.errors.${result.code}`, result.code));
      return;
    }

    const body: AdminClaimProcessRequest = {};
    if (note.trim().length > 0) body.note = note.trim();
    if (action === 'reject') body.reasonCode = reasonCode;
    if (action === 'approve' && overrideEnabled) {
      const parsed = Number.parseInt(overrideAmount, 10);
      if (Number.isNaN(parsed) || parsed < 0) {
        setLocalError(t('claims.actionModal.invalidAmount'));
        return;
      }
      const currency = expectedRefundAmount?.currencyCode ?? 'KRW';
      body.refundOverrideAmount = { amount: parsed, currencyCode: currency };
    }
    setLocalError(null);
    onSubmit(body);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-claim-action={action} className="max-w-md">
        <DialogTitle>{t(`claims.actions.${action}`)}</DialogTitle>
        <DialogDescription>
          {t('claims.actionModal.subject', { orderNo, claimId: claimId.slice(0, 8) })}
        </DialogDescription>

        {action === 'approve' ? (
          <div
            className="mt-3 rounded-md p-3 text-xs"
            style={{
              background: 'var(--status-info-bg)',
              color: 'var(--status-info-fg)',
            }}
            role="note"
          >
            {t('claims.actionModal.approveAutoRefundNotice')}
          </div>
        ) : null}

        {action === 'complete' ? (
          <div
            className="mt-3 rounded-md p-3 text-xs"
            style={{
              background: 'var(--status-warn-bg)',
              color: 'var(--status-warn-fg)',
            }}
            role="note"
          >
            {t('claims.actionModal.completeManualNotice')}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {action === 'reject' ? (
            <div className="flex flex-col gap-2">
              <span
                id="claim-action-reason-label"
                className="text-sm text-[color:var(--content-muted)]"
              >
                {t('claims.actionModal.rejectReasonCode')}
                <span aria-hidden="true" className="ml-1 text-[color:var(--status-danger-fg)]">
                  *
                </span>
              </span>
              <RadioGroup
                value={reasonCode}
                onValueChange={(v) => setReasonCode(v as ClaimReason)}
                options={REJECT_REASON_OPTIONS.map((r) => ({
                  value: r,
                  label: t(`claims.reasonCode.${r}`),
                }))}
                orientation="horizontal"
                aria-labelledby="claim-action-reason-label"
                data-testid="claim-action-reason"
              />
            </div>
          ) : null}

          {action === 'approve' ? (
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-[160px_1fr] items-baseline gap-2 text-sm">
                <span className="text-[color:var(--content-muted)]">
                  {t('claims.actionModal.expectedRefundAmount')}
                </span>
                <span
                  className="font-medium text-[color:var(--content-strong)]"
                  data-testid="claim-expected-refund"
                >
                  {formatMoney(expectedRefundAmount)}
                </span>
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-[color:var(--content-muted)]">
                <input
                  type="checkbox"
                  checked={overrideEnabled}
                  onChange={(e) => setOverrideEnabled(e.target.checked)}
                  data-testid="claim-refund-override-toggle"
                />
                {t('claims.actionModal.refundOverrideToggle')}
              </label>
              {overrideEnabled ? (
                <div className="flex flex-col">
                  <label
                    htmlFor="claim-refund-override"
                    className="text-sm text-[color:var(--content-muted)]"
                  >
                    {t('claims.actionModal.refundOverrideAmount')}
                  </label>
                  <Input
                    id="claim-refund-override"
                    type="number"
                    min={0}
                    value={overrideAmount}
                    onChange={(e) => setOverrideAmount(e.target.value)}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {action === 'collect' ? (
            <div
              className="rounded-md p-3 text-xs"
              style={{
                background: 'var(--status-progress-bg)',
                color: 'var(--status-progress-fg)',
              }}
              role="note"
            >
              {t('claims.actionModal.collectNotice')}
            </div>
          ) : null}

          <div className="flex flex-col">
            <label
              htmlFor="claim-action-note"
              className="text-sm text-[color:var(--content-muted)]"
            >
              {t('claims.actionModal.note')}
              {action === 'reject' ? (
                <span aria-hidden="true" className="ml-1 text-[color:var(--status-danger-fg)]">
                  *
                </span>
              ) : null}
            </label>
            <textarea
              id="claim-action-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
              rows={4}
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              style={{ borderColor: 'var(--surface-1)' }}
            />
          </div>

          {localError ? (
            <p
              className="text-sm"
              role="alert"
              style={{ color: 'var(--status-danger-fg)' }}
            >
              {localError}
            </p>
          ) : null}

          {errorMessage ? (
            <p
              className="text-sm"
              role="alert"
              style={{ color: 'var(--status-danger-fg)' }}
            >
              {errorMessage}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('claims.actions.cancel')}
            </Button>
            <Button type="submit" variant="cta" size="md" disabled={isSubmitting}>
              {isSubmitting
                ? t('claims.actionModal.submitting')
                : t('claims.actionModal.confirm')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
